import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, CheckCircle, Clock, Bell, BellOff,
  ArrowLeft, Filter, Download, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AlertsPage = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unresolvedAlerts, setUnresolvedAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unresolved, resolved

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      if (!token) {
        navigate('/monitoring-dashboard/login');
        return;
      }

      // Fetch alert history
      const historyResponse = await fetch(`${API_URL}/api/monitoring/alerts/history?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setAlerts(historyData);
      }

      // Fetch unresolved alerts
      const unresolvedResponse = await fetch(`${API_URL}/api/monitoring/alerts/unresolved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (unresolvedResponse.ok) {
        const unresolvedData = await unresolvedResponse.json();
        setUnresolvedAlerts(unresolvedData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Failed to load alerts');
      setLoading(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unresolved') return !alert.resolved;
    if (filter === 'resolved') return alert.resolved;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    // Convert to Indian timezone (IST - UTC+5:30)
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getAlertIcon = (status) => {
    if (status === 'down') {
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    }
    return <CheckCircle className="h-5 w-5 text-green-600" />;
  };

  const stats = {
    total: alerts.length,
    unresolved: unresolvedAlerts.length,
    resolved: alerts.filter(a => a.resolved).length,
    downAlerts: alerts.filter(a => a.status === 'down').length,
    recoveryAlerts: alerts.filter(a => a.status === 'recovered').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/monitoring-dashboard')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              {/* Netix Logo */}
              <div className="bg-white rounded-lg p-2 shadow-md">
                <img 
                  src="/netix-logo-horizontal.png" 
                  alt="Netix Fibernet" 
                  className="h-8 w-auto"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Bell className="h-6 w-6" />
                  Alert History
                </h1>
                <p className="text-sm text-white/80">System-wide alert notifications and history</p>
              </div>
            </div>
            <Button
              onClick={fetchAlerts}
              size="sm"
              className="bg-white text-red-600 hover:bg-red-50"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Alerts</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Bell className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unresolved</p>
                  <p className="text-2xl font-bold text-red-600">{stats.unresolved}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Resolved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Down Alerts</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.downAlerts}</p>
                </div>
                <BellOff className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Recoveries</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.recoveryAlerts}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
          >
            All ({stats.total})
          </Button>
          <Button
            onClick={() => setFilter('unresolved')}
            variant={filter === 'unresolved' ? 'default' : 'outline'}
            size="sm"
            className={filter === 'unresolved' ? 'bg-red-600' : ''}
          >
            Unresolved ({stats.unresolved})
          </Button>
          <Button
            onClick={() => setFilter('resolved')}
            variant={filter === 'resolved' ? 'default' : 'outline'}
            size="sm"
            className={filter === 'resolved' ? 'bg-green-600' : ''}
          >
            Resolved ({stats.resolved})
          </Button>
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Alert History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading alerts...</div>
            ) : filteredAlerts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No alerts found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredAlerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      alert.status === 'down'
                        ? 'bg-red-50 border-red-500'
                        : 'bg-green-50 border-green-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getAlertIcon(alert.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {alert.office_name}
                            </h3>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                alert.status === 'down'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {alert.status === 'down' ? 'DOWN' : 'RECOVERED'}
                            </span>
                            {!alert.resolved && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-semibold">
                                ACTIVE
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            Customer: {alert.customer_name}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">IP:</span>{' '}
                              <code className="text-gray-900">{alert.static_ip}</code>
                            </div>
                            {alert.consecutive_failures > 0 && (
                              <div>
                                <span className="text-gray-500">Failed Attempts:</span>{' '}
                                <span className="text-red-600 font-semibold">
                                  {alert.consecutive_failures}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1 text-gray-600 mb-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(alert.created_at)}
                        </div>
                        {alert.resolved && alert.resolved_at && (
                          <div className="text-xs text-green-600">
                            Resolved: {formatDate(alert.resolved_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlertsPage;
